import { TICKS_PER_BAR, TICKS_PER_BEAT } from '../../shared/tempo';
import type { ClassDefinition, UpgradeDefinition } from '../types';

export interface BroodAttack {
  kind: 'brood';
  damage: number;
  cooldownTicks: number;
  chicks: number;
  turnBeats: number;
  pecks: number;
  peckTicks: number;
  gap: number;
  reach: number;
  range: number;
  speed: number;
  returnSpeed: number;
}

export interface DebandadeEffect {
  kind: 'debandade';
  damage: number;
  length: number;
  speed: number;
  spacing: number;
  radius: number;
  knockBeats: number;
}

export const POUSSIN: ClassDefinition = {
  id: 'poussin',
  name: 'Le Poussin',
  role: 'Sa bande de poussins le suit en file, et file picorer les bad vibes.',
  color: '#ffe14d',
  maxHp: 130,
  speed: 7.4,
  radius: 14,
  pickupRadius: 120,
  attack: {
    kind: 'brood',
    damage: 12,
    cooldownTicks: TICKS_PER_BEAT,
    chicks: 3,
    turnBeats: 3,
    pecks: 4,
    peckTicks: 3,
    gap: 22,
    reach: 140,
    range: 100,
    speed: 18,
    returnSpeed: 14,
  },
  skill: {
    id: 'debandade',
    name: 'Débandade',
    description: 'Toute ta bande charge d’un coup là où tu vises et renverse ce qu’elle piétine.',
    cooldownTicks: 4 * TICKS_PER_BAR,
    effect: {
      kind: 'debandade',
      damage: 15,
      length: 250,
      speed: 14,
      spacing: 26,
      radius: 9,
      knockBeats: 1,
    },
  },
  affinityWeaponIds: ['monocycle'],
};

export const POUSSIN_UPGRADES: readonly UpgradeDefinition[] = [
  {
    id: 'couvee-nombreuse',
    name: 'Couvée nombreuse',
    description: 'Un poussin de plus marche dans ta bande.',
    family: 'class',
    classId: 'poussin',
    modifiers: [{ stat: 'projectileCountAdd', add: 1 }],
    rarities: {
      rare: {
        description: 'Deux poussins de plus marchent dans ta bande.',
        modifiers: [{ stat: 'projectileCountAdd', add: 2 }],
      },
      legendary: {
        description: 'Trois poussins de plus marchent dans ta bande.',
        modifiers: [{ stat: 'projectileCountAdd', add: 3 }],
      },
    },
    maxStacks: 2,
  },
  {
    id: 'petit-creux',
    name: 'Petit creux',
    description: 'Tes poussins donnent un coup de bec de plus.',
    family: 'class',
    classId: 'poussin',
    modifiers: [{ stat: 'pierceAdd', add: 1 }],
    rarities: {
      rare: {
        description: 'Tes poussins donnent deux coups de bec de plus.',
        modifiers: [{ stat: 'pierceAdd', add: 2 }],
      },
      legendary: {
        description: 'Tes poussins donnent trois coups de bec de plus.',
        modifiers: [{ stat: 'pierceAdd', add: 3 }],
      },
    },
    maxStacks: 2,
  },
  {
    id: 'cavalcade',
    name: 'Cavalcade',
    description: 'Ta débandade piétine plus fort.',
    family: 'class',
    classId: 'poussin',
    modifiers: [{ stat: 'skillPowerMul', mul: 1.55 }],
    rarities: {
      rare: {
        description: 'Ta débandade piétine deux fois plus fort.',
        modifiers: [{ stat: 'skillPowerMul', mul: 2.25 }],
      },
      legendary: {
        description: 'Ta débandade piétine trois fois plus fort.',
        modifiers: [{ stat: 'skillPowerMul', mul: 3 }],
      },
    },
    maxStacks: 2,
  },
  {
    id: 'remontes-a-bloc',
    name: 'Remontés à bloc',
    description: 'Ta débandade revient plus vite.',
    family: 'class',
    classId: 'poussin',
    modifiers: [{ stat: 'skillCooldownMul', mul: 0.64 }],
    rarities: {
      rare: {
        description: 'Ta débandade revient bien plus vite.',
        modifiers: [{ stat: 'skillCooldownMul', mul: 0.36 }],
      },
      legendary: {
        description: 'Ta débandade revient presque aussitôt.',
        modifiers: [{ stat: 'skillCooldownMul', mul: 0.16 }],
      },
    },
    maxStacks: 2,
  },
];
